"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.subModelAccessors = exports.subModelFilterSchema = exports.differentialPairAccessors = exports.differentialPairFilterSchema = exports.boundaryAccessors = exports.boundaryFilterSchema = exports.resourceAccessors = exports.resourceFilterSchema = exports.functionAccessors = exports.functionFilterSchema = exports.signalAccessors = exports.signalFilterSchema = void 0;
const QueryBuilder_1 = require("../QueryBuilder");
exports.signalFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
    fields: {
        id: {
            description: 'Signal id (opaque). Prefer `name` for searches.',
            options: { type: QueryBuilder_1.FieldType.String },
            allowedOperators: ['equals', 'is', 'isAnyOf', 'not', 'doesNotEqual'],
        },
        name: {
            description: 'Signal name (e.g. VCC, SDA). Searchable as text.',
            options: { type: QueryBuilder_1.FieldType.String },
        },
        description: {
            description: 'Signal description text.',
            options: { type: QueryBuilder_1.FieldType.String },
        },
        signalDirection: {
            description: 'Signal direction (input, output, bidirectional, etc.).',
            options: { type: QueryBuilder_1.FieldType.String },
            allowedOperators: ['equals', 'is', 'isAnyOf', 'not', 'doesNotEqual', 'isEmpty', 'isNotEmpty'],
        },
        floatingBehavior: {
            description: 'Whether the signal can be left floating (Always, Sometimes, Never, Conditional, Unspecified).',
            options: { type: QueryBuilder_1.FieldType.String },
            allowedOperators: ['equals', 'is', 'isAnyOf', 'not', 'doesNotEqual', 'isEmpty', 'isNotEmpty'],
        },
        isCadstromInternal: {
            description: 'True if the signal is a Cadstrom-internal construct (parser-only).',
            options: { type: QueryBuilder_1.FieldType.Boolean },
        },
        padCount: {
            description: 'Number of pad references mapped to this signal.',
            options: { type: QueryBuilder_1.FieldType.Number },
        },
        pinCount: {
            description: 'Number of (non-null) symbol pin references on this signal.',
            options: { type: QueryBuilder_1.FieldType.Number },
        },
        driveModeCount: {
            description: 'Number of supported drive modes declared on this signal.',
            options: { type: QueryBuilder_1.FieldType.Number },
        },
        driveModeType: {
            description: 'Drive mode type literal (e.g. push-pull, open-drain, high-z). Matches if any supported drive mode has the given type.',
            options: { type: QueryBuilder_1.FieldType.String },
            allowedOperators: ['equals', 'is', 'isAnyOf', 'contains'],
        },
    },
});
exports.signalAccessors = {
    id: (s) => String(s.id),
    name: (s) => s.name,
    description: (s) => s.description ?? null,
    signalDirection: (s) => s.signalDirection ?? null,
    floatingBehavior: (s) => s.floatingBehavior ?? null,
    isCadstromInternal: (s) => s.isCadstromInternal ?? false,
    padCount: (s) => s.padRefs.length,
    pinCount: (s) => s.pinRefs.filter((p) => p !== null).length,
    driveModeCount: (s) => s.supportedDriveModes?.length ?? 0,
    driveModeType: (s) => s.supportedDriveModes?.map((m) => m.type) ?? [],
};
exports.functionFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
    fields: {
        name: {
            description: 'Function name (canonical identifier on IComponentFunction).',
            options: { type: QueryBuilder_1.FieldType.String },
        },
        functionClass: {
            description: 'Function class identifier (e.g. bus.i2c.controller).',
            options: { type: QueryBuilder_1.FieldType.String },
        },
        busRole: {
            description: 'Bus role for bus functions (e.g. controller, target).',
            options: { type: QueryBuilder_1.FieldType.String },
            allowedOperators: ['equals', 'is', 'isAnyOf', 'not', 'doesNotEqual', 'isEmpty', 'isNotEmpty'],
        },
        mustBeAssigned: {
            description: 'True if this function must be assigned during resolution.',
            options: { type: QueryBuilder_1.FieldType.Boolean },
        },
        resourceConsumed: {
            description: 'Name of the resource this function consumes, if any.',
            options: { type: QueryBuilder_1.FieldType.String },
        },
        functionSignalCount: {
            description: 'Number of function signals declared on this function.',
            options: { type: QueryBuilder_1.FieldType.Number },
        },
    },
});
exports.functionAccessors = {
    name: (f) => f.name,
    functionClass: (f) => f.functionClass ?? null,
    busRole: (f) => f.busRole ?? null,
    mustBeAssigned: (f) => f.mustBeAssigned ?? false,
    resourceConsumed: (f) => f.resourceConsumed ?? null,
    functionSignalCount: (f) => f.functionSignals.length,
};
exports.resourceFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
    fields: {
        name: {
            description: 'Resource name (canonical identifier on IComponentResource).',
            options: { type: QueryBuilder_1.FieldType.String },
        },
        mustBeConsumed: {
            description: 'True if a function must consume this resource.',
            options: { type: QueryBuilder_1.FieldType.Boolean },
        },
    },
});
exports.resourceAccessors = {
    name: (r) => r.name,
    mustBeConsumed: (r) => r.mustBeConsumed ?? false,
};
exports.boundaryFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
    fields: {
        id: {
            description: 'Boundary id.',
            options: { type: QueryBuilder_1.FieldType.String },
        },
        global: {
            description: 'True if the boundary is global.',
            options: { type: QueryBuilder_1.FieldType.Boolean },
        },
        class: {
            description: 'Boundary class, if present.',
            options: { type: QueryBuilder_1.FieldType.String },
            allowedOperators: ['equals', 'is', 'isAnyOf', 'not', 'doesNotEqual', 'isEmpty', 'isNotEmpty', 'contains'],
        },
    },
});
exports.boundaryAccessors = {
    id: (b) => b.id,
    global: (b) => b.global ?? false,
    class: (b) => b.boundary.class ?? null,
};
exports.differentialPairFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
    fields: {
        name: {
            description: 'Differential pair name (canonical identifier).',
            options: { type: QueryBuilder_1.FieldType.String },
        },
        positiveSignalId: {
            description: 'Signal id of the positive signal.',
            options: { type: QueryBuilder_1.FieldType.String },
            allowedOperators: ['equals', 'is', 'isAnyOf', 'not', 'doesNotEqual', 'isEmpty', 'isNotEmpty'],
        },
        negativeSignalId: {
            description: 'Signal id of the negative signal.',
            options: { type: QueryBuilder_1.FieldType.String },
            allowedOperators: ['equals', 'is', 'isAnyOf', 'not', 'doesNotEqual', 'isEmpty', 'isNotEmpty'],
        },
    },
});
exports.differentialPairAccessors = {
    name: (d) => d.pairName,
    positiveSignalId: (d) => String(d.positiveId),
    negativeSignalId: (d) => String(d.negativeId),
};
exports.subModelFilterSchema = (0, QueryBuilder_1.createFilterSchema)({
    fields: {
        subModelId: {
            description: 'Unique id for this submodel.',
            options: { type: QueryBuilder_1.FieldType.String },
        },
        type: {
            description: 'Submodel type: "spice" for SPICE netlists, "sigma" for Sigma sub-models.',
            options: { type: QueryBuilder_1.FieldType.String },
            allowedOperators: ['equals', 'is', 'isAnyOf'],
        },
    },
});
exports.subModelAccessors = {
    subModelId: (m) => m.subModelId,
    type: (m) => m.type,
};
//# sourceMappingURL=filter-schemas.js.map